import { Controller } from '@hotwired/stimulus';

// The chokepoint fragment the `update_modal` stream replaces
// (`MeetingReports::ShareModalComponent::CONTENT_ID`). A stream aimed anywhere else (the live
// region `cr-share-modal-live` included) leaves the receipt state alone.
const CONTENT_ID = 'cr-share-modal-content';

// The polite live region the `update_modal` stream fills with the server's message
// (`MeetingReports::ShareModalComponent::LIVE_REGION_ID`), outside the replaced content.
const LIVE_REGION_ID = 'cr-share-modal-live';

const FILTERS = ['all', 'read', 'unread'];

// Delay between clearing the live region and writing the message again: a screen reader does not
// re-read a polite region whose text did not change, so a repeated copy or server message needs an
// empty tick first.
const ANNOUNCE_DELAY_MS = 150;

// Focus candidates inside a `data-share-focus-key` holder. No pseudo-class: `tabindex="-1"` and
// `disabled` are filtered in JS (`#focusCandidates`).
const FOCUSABLE_SELECTOR = 'button, a[href], input, select, textarea, summary, [tabindex]';

/**
 * Whether a recipient row stays visible under a read filter.
 * « Tous » keeps every row; « Lus » / « Pas encore lus » keep their own state only, so a
 * `not_applicable` row (served via another party) shows under « Tous » alone.
 *
 * @param {string} readState `read` | `unread` | `not_applicable` (`data-read-state`)
 * @param {string} filter `all` | `read` | `unread` (`data-value` of the chip)
 * @returns {boolean}
 */
export function rowMatchesFilter(readState, filter) {
  if (filter === 'all') return true;
  if (filter !== 'read' && filter !== 'unread') return false;
  return readState === filter;
}

/**
 * Applies a read filter to the colleges of the sent list (pure, no DOM).
 *
 * @param {string[][]} colleges the `data-read-state` of each row, college by college
 * @param {string} filter `all` | `read` | `unread`
 * @returns {{ colleges: { visible: boolean[], count: number, hidden: boolean }[], total: number, empty: string|null }}
 *   per college the visibility of each row, the visible count (rewritten in its `<summary>`) and
 *   whether the college is hidden (no visible row); `total` visible rows; `empty` = the filter whose
 *   empty message shows (only an active filter that matches nothing), else `null`.
 */
export function filterColleges(colleges, filter) {
  let total = 0;
  const result = colleges.map((states) => {
    const visible = states.map((state) => rowMatchesFilter(state, filter));
    const count = visible.filter(Boolean).length;
    total += count;
    return { visible, count, hidden: count === 0 };
  });
  const empty = filter !== 'all' && total === 0 ? filter : null;
  return { colleges: result, total, empty };
}

/**
 * Share Receipt Controller — the « Diffusion du CR » modal (`MeetingReports::ShareModalComponent`).
 *
 * Filters the sent list by read state, keeps the user's view across the `update_modal` chokepoint
 * re-render (filter, open « À vérifier », collapsed colleges, focus) and announces copy results.
 * Every user-visible text comes from values (i18n, server side): the controller writes no copy of
 * its own, and only through `textContent`.
 *
 * No `data-action` in the markup: the host listens in `connect()` (click, focusin,
 * `clipboard:copied`, `clipboard:failed`, `modal:open`, `modal:close`) and on `document`
 * (`turbo:before-stream-render`), all removed in `disconnect()`.
 *
 * Targets:
 * - chip: filter button, `data-value="all|read|unread"`, `aria-pressed` (single selection)
 * - row: recipient row, `data-read-state="read|unread|not_applicable"`
 * - college: `<details data-college="<key>">`, its `<summary>` holds `[data-share-college-count]`
 *   (rewritten with the visible rows of the college)
 * - issues: the « À vérifier » `<details>` (reopened after a re-render if the user had opened it)
 * - empty: message per filter, `data-filter="read|unread"`, shown when that filter matches nothing
 * - announcer: the polite live region, OUTSIDE the stream fragment (never replaced by it)
 * - alert: the refusal re-rendered at the top of the content (422), brought into view after a
 *   re-render; never the permanent provenance bands (`role="alert"` too, but no target). The 422
 *   empties the live region instead of copying the alert: `role="alert"` already announces it.
 *
 * Values:
 * - copiedText: announced on `clipboard:copied` (bubbling from a copy button or menu item)
 * - copyFailedText: announced on `clipboard:failed`
 *
 * Behaviour:
 * - Colleges under a filter: a college with matches opens, one without is hidden; back on « Tous »
 *   each college returns to the user's own collapse. The collapse intent is read from `open` on the
 *   `<summary>` click, BEFORE the native toggle, never from the `toggle` event (scripted changes
 *   fire it too).
 * - Server announcement (a stream aimed at `#cr-share-modal-live`): its render is wrapped so the
 *   region is emptied first and the stream renders after `ANNOUNCE_DELAY_MS`, like a copy
 *   announcement: an identical repeat (« Lien révoqué. » twice) is re-read. A later announcement
 *   cancels a pending one.
 * - Re-render of `#cr-share-modal-content`: the state is captured on `turbo:before-stream-render`,
 *   the stream's render is wrapped and the state reapplied to the NEW content. Focus goes back to the
 *   element carrying the last `data-share-focus-key` seen on `focusin` (or the same focusable inside
 *   it); key gone, or refusal content without hooks: the dialog title named by `aria-labelledby`
 *   (`tabindex="-1"` set here). Focus outside the dialog at render time (the confirmation modal's
 *   button, hidden 200 ms after its submit) counts as lost. A refusal alert in the new content is then
 *   scrolled into view (`block: 'nearest'`), focus unchanged, and the restored control after it, so a
 *   low row is not pushed off screen. Dialog closed by the user before the stream (`modal:close`, or the
 *   `hidden` class): filter reapplied, focus and scroll left alone.
 *
 * Events: none dispatched.
 *
 * @example
 * <div role="dialog" aria-labelledby="cr-share-modal-title" data-dropdown-portal-host
 *      data-controller="modal share-receipt"
 *      data-share-receipt-copied-text-value="…" data-share-receipt-copy-failed-text-value="…">
 *   <h3 id="cr-share-modal-title">…</h3>
 *   <div id="cr-share-modal-content">
 *     <details data-share-receipt-target="issues">…</details>
 *     <div role="group" aria-label="…">
 *       <button type="button" data-share-receipt-target="chip" data-value="all" aria-pressed="true"
 *               data-share-focus-key="chip-all"><span>…</span></button>
 *     </div>
 *     <p data-share-receipt-target="empty" data-filter="read" hidden>…</p>
 *     <details data-share-receipt-target="college" data-college="moa" open>
 *       <summary>… <span data-share-college-count>2</span></summary>
 *       <ul><li data-share-receipt-target="row" data-read-state="read" data-share-focus-key="…">…</li></ul>
 *     </details>
 *   </div>
 *   <div id="cr-share-modal-live" role="status" aria-live="polite"
 *        data-share-receipt-target="announcer"></div>
 * </div>
 */
export default class extends Controller {
  static targets = ['chip', 'row', 'college', 'issues', 'empty', 'announcer', 'alert'];
  static values = {
    copiedText: String,
    copyFailedText: String,
  };

  connect() {
    const pressed = this.chipTargets.find((chip) => chip.getAttribute('aria-pressed') === 'true');
    this.filter = FILTERS.includes(pressed?.dataset.value) ? pressed.dataset.value : 'all';
    // College keys the user collapsed under « Tous », and under the current filter (cleared on
    // every filter change, so a filter always opens the colleges it keeps).
    this.collapsed = new Set();
    this.collapsedUnderFilter = new Set();
    this.focusKey = null;
    this.focusIndex = -1;
    this.announceTimeout = null;
    this.announceResolve = null;
    // Set by `modal:close` (the close animation runs 200 ms before the `hidden` class), cleared by
    // `modal:open`: both dispatched by `modal_controller.js` on this same root.
    this.closing = false;

    this.onClick = this.handleClick.bind(this);
    this.onFocusIn = this.handleFocusIn.bind(this);
    this.onCopied = this.handleCopied.bind(this);
    this.onCopyFailed = this.handleCopyFailed.bind(this);
    this.onBeforeStreamRender = this.handleBeforeStreamRender.bind(this);
    this.onModalOpen = () => {
      this.closing = false;
    };
    this.onModalClose = () => {
      this.closing = true;
    };

    this.element.addEventListener('click', this.onClick);
    this.element.addEventListener('modal:open', this.onModalOpen);
    this.element.addEventListener('modal:close', this.onModalClose);
    this.element.addEventListener('focusin', this.onFocusIn);
    this.element.addEventListener('clipboard:copied', this.onCopied);
    this.element.addEventListener('clipboard:failed', this.onCopyFailed);
    document.addEventListener('turbo:before-stream-render', this.onBeforeStreamRender);

    if (this.filter !== 'all') this.#applyFilter();
  }

  disconnect() {
    this.element.removeEventListener('click', this.onClick);
    this.element.removeEventListener('focusin', this.onFocusIn);
    this.element.removeEventListener('clipboard:copied', this.onCopied);
    this.element.removeEventListener('clipboard:failed', this.onCopyFailed);
    this.element.removeEventListener('modal:open', this.onModalOpen);
    this.element.removeEventListener('modal:close', this.onModalClose);
    document.removeEventListener('turbo:before-stream-render', this.onBeforeStreamRender);
    this.#clearAnnounceTimeout();
  }

  // --- Host listeners --------------------------------------------------------------------------

  handleClick(event) {
    const target = event.target;
    if (typeof target?.closest !== 'function') return;

    const chip = target.closest('[data-share-receipt-target~="chip"]');
    if (chip && this.chipTargets.includes(chip)) {
      this.#selectFilter(chip.dataset.value);
      return;
    }

    const summary = target.closest('summary');
    const details = summary?.parentElement;
    if (details && this.collegeTargets.includes(details)) this.#recordCollapseIntent(details);
  }

  handleFocusIn(event) {
    const target = event.target;
    if (typeof target?.closest !== 'function') return;

    const holder = target.closest('[data-share-focus-key]');
    if (holder && this.element.contains(holder)) {
      this.focusKey = holder.getAttribute('data-share-focus-key');
      this.focusIndex = this.#focusCandidates(holder).indexOf(target);
      return;
    }
    // Focus inside the receipt but on nothing keyed (public link block…): nothing to come back to.
    // Outside the fragment (title, footer, a menu portalled into the dialog) the key is kept.
    if (this.#content()?.contains(target)) {
      this.focusKey = null;
      this.focusIndex = -1;
    }
  }

  handleCopied() {
    this.#announce(this.hasCopiedTextValue ? this.copiedTextValue : '');
  }

  handleCopyFailed() {
    this.#announce(this.hasCopyFailedTextValue ? this.copyFailedTextValue : '');
  }

  handleBeforeStreamRender(event) {
    const stream = event.target;
    const render = event.detail?.render;
    if (typeof render !== 'function') return;
    if (this.#targetsLiveRegion(stream)) {
      event.detail.render = this.#deferredAnnouncement(render);
      return;
    }
    if (!this.#replacesContent(stream)) return;

    const issuesOpen = this.hasIssuesTarget && this.issuesTarget.open;
    event.detail.render = async (element) => {
      await render(element);
      this.#restore({ issuesOpen });
    };
  }

  // --- Filter ----------------------------------------------------------------------------------

  #selectFilter(value) {
    if (!FILTERS.includes(value)) return;
    if (value !== this.filter) this.collapsedUnderFilter.clear();
    this.filter = value;
    this.#applyFilter();
  }

  #applyFilter() {
    const filter = this.hasChipTarget ? this.filter : 'all';

    this.chipTargets.forEach((chip) => {
      chip.setAttribute('aria-pressed', String(chip.dataset.value === filter));
    });

    const colleges = this.collegeTargets;
    const rowsByCollege = colleges.map((college) =>
      this.rowTargets.filter((row) => college.contains(row))
    );
    const result = filterColleges(
      rowsByCollege.map((rows) => rows.map((row) => row.dataset.readState)),
      filter
    );

    colleges.forEach((college, index) => {
      const { visible, count, hidden } = result.colleges[index];
      rowsByCollege[index].forEach((row, rowIndex) => {
        row.hidden = !visible[rowIndex];
      });
      college.hidden = hidden;
      const counter = college.querySelector('[data-share-college-count]');
      if (counter) counter.textContent = String(count);

      const open = !this.#collapsedFor(filter).has(college.dataset.college);
      if (college.open !== open) college.open = open;
    });

    this.emptyTargets.forEach((message) => {
      message.hidden = message.dataset.filter !== result.empty;
    });
  }

  // Runs on the `<summary>` click, before the browser flips `open`: `open` still holds the state the
  // user is leaving.
  #recordCollapseIntent(college) {
    const key = college.dataset.college;
    if (!key) return;
    const collapsed = this.#collapsedFor(this.filter);
    if (college.open) collapsed.add(key);
    else collapsed.delete(key);
  }

  // The user's collapse under « Tous » is kept across filters; the one under a filter is not.
  #collapsedFor(filter) {
    return filter === 'all' ? this.collapsed : this.collapsedUnderFilter;
  }

  // --- Re-render of the content ----------------------------------------------------------------

  #content() {
    const content = document.getElementById(CONTENT_ID);
    return content && this.element.contains(content) ? content : null;
  }

  #replacesContent(stream) {
    return this.#streamTargets(stream, CONTENT_ID);
  }

  #targetsLiveRegion(stream) {
    return this.#streamTargets(stream, LIVE_REGION_ID);
  }

  #streamTargets(stream, id) {
    const targets = stream?.targetElements;
    if (!targets) return false;
    return [...targets].some((node) => node.id === id && this.element.contains(node));
  }

  #restore({ issuesOpen }) {
    if (!this.element.isConnected) return;

    if (issuesOpen && this.hasIssuesTarget && !this.issuesTarget.open) {
      this.issuesTarget.open = true;
    }
    this.#applyFilter();
    // Closed by the user (Échap, « Fermer ») before the stream landed: `modal#close` gives focus back to
    // the opener, nothing to restore or scroll in a dialog that is going away.
    if (!this.#dialogOpen()) return;

    const restored = this.#restoreFocus();
    // A refusal re-rendered at the top of the content (422) while the dialog kept its scroll: brought
    // into view (its role=alert announces it). Then the control focus was restored to, LAST: both stay
    // visible when they fit, focus wins when they do not (WCAG 2.4.11). Never the title, above the body.
    if (this.hasAlertTarget) {
      this.alertTarget.scrollIntoView({ block: 'nearest' });
      restored?.scrollIntoView({ block: 'nearest' });
    }
  }

  // Open the way `modal_controller.js` keeps it: the root without its `hidden` class, and no
  // `modal:close` since the last `modal:open` (the 200 ms close animation).
  #dialogOpen() {
    return !this.closing && !this.element.classList.contains('hidden');
  }

  // Returns the keyed control focus was restored to, or null (focus kept, or sent to the title).
  #restoreFocus() {
    const active = document.activeElement;
    // Focus that survived the swap on a control of the dialog outside the fragment (title, footer, a
    // menu portalled into the dialog) stays where the user put it. Focus OUTSIDE the dialog is the
    // confirmation modal's button (under <body>): its `close()` hides it 200 ms after the submit, often
    // after this stream, and focus would then fall to <body>. Treated as lost.
    if (active && active !== document.body && active.isConnected && this.element.contains(active)) {
      return null;
    }

    const holder = this.#focusHolder();
    if (holder && this.#focusWithin(holder)) return document.activeElement;
    this.#focusTitle();
    return null;
  }

  #focusHolder() {
    if (!this.focusKey) return null;
    const content = this.#content();
    if (!content) return null;
    // Compared in JS rather than through an attribute selector: the key needs no escaping.
    const holders = [...content.querySelectorAll('[data-share-focus-key]')];
    return (
      holders.find((node) => node.getAttribute('data-share-focus-key') === this.focusKey) || null
    );
  }

  // The same focusable as before when the holder kept its shape, else the holder, else the first
  // focusable inside it. `focus()` is silently refused on a node that is not rendered: check it.
  #focusWithin(holder) {
    const candidates = this.#focusCandidates(holder);
    const ordered = [candidates[this.focusIndex], holder, ...candidates].filter(Boolean);
    return ordered.some((node) => {
      node.focus();
      return document.activeElement === node;
    });
  }

  #focusCandidates(holder) {
    const nodes = [holder, ...holder.querySelectorAll(FOCUSABLE_SELECTOR)];
    return nodes.filter(
      (node) =>
        node.matches(FOCUSABLE_SELECTOR) &&
        node.getAttribute('tabindex') !== '-1' &&
        !node.hasAttribute('disabled')
    );
  }

  #focusTitle() {
    const dialog = this.element.closest('[aria-labelledby]');
    const titleId = dialog?.getAttribute('aria-labelledby');
    const title = titleId ? document.getElementById(titleId) : null;
    if (!title) return;
    if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
    title.focus();
  }

  // --- Announcements ---------------------------------------------------------------------------

  // Wraps the live-region stream's own render: the region is emptied now and the stream renders its
  // (server-escaped) text after the delay. The promise always settles, cancelled or not, so Turbo
  // never keeps a `<turbo-stream>` waiting.
  #deferredAnnouncement(render) {
    return (element) =>
      new Promise((resolve) => {
        this.#clearAnnounceTimeout();
        if (this.hasAnnouncerTarget) this.announcerTarget.textContent = '';
        this.announceResolve = resolve;
        this.announceTimeout = setTimeout(async () => {
          this.announceTimeout = null;
          this.announceResolve = null;
          try {
            await render(element);
          } finally {
            resolve();
          }
        }, ANNOUNCE_DELAY_MS);
      });
  }

  #announce(text) {
    if (!this.hasAnnouncerTarget || !text) return;
    this.#clearAnnounceTimeout();
    const announcer = this.announcerTarget;
    announcer.textContent = '';
    this.announceTimeout = setTimeout(() => {
      this.announceTimeout = null;
      announcer.textContent = text;
    }, ANNOUNCE_DELAY_MS);
  }

  #clearAnnounceTimeout() {
    if (this.announceTimeout) {
      clearTimeout(this.announceTimeout);
      this.announceTimeout = null;
    }
    // A cancelled server announcement still settles its stream's render.
    const resolve = this.announceResolve;
    this.announceResolve = null;
    resolve?.();
  }
}
